/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { getApiKey } from './api-keys'

export interface GitHubRepoInfo {
  owner: string
  repo: string
  fullName: string
}

/**
 * Parse GitHub repository URL to extract owner and repo name
 */
export function parseGitHubUrl(url: string): GitHubRepoInfo | null {
  // Support various GitHub URL formats:
  // - https://github.com/owner/repo
  // - https://github.com/owner/repo.git
  // - git@github.com:owner/repo.git
  // - owner/repo
  
  let match = url.match(/github\.com[:/]([^/]+)\/([^/]+?)(?:\.git)?$/)
  
  if (!match) {
    // Try simple owner/repo format
    match = url.match(/^([^/]+)\/([^/]+)$/)
  }
  
  if (!match) {
    return null
  }
  
  const owner = match[1]
  const repo = match[2]
  
  return {
    owner,
    repo,
    fullName: `${owner}/${repo}`
  }
}

/**
 * Recursively fetches all files from a GitHub repository directory
 */
async function fetchRepoContents(
  owner: string,
  repo: string,
  path: string,
  branch: string,
  headers: HeadersInit,
  files: Map<string, string>,
  onProgress?: (message: string, progress: number) => void
): Promise<void> {
  const url = `https://api.github.com/repos/${owner}/${repo}/contents/${path}?ref=${branch}`
  const response = await fetch(url, { headers })

  if (!response.ok) {
    throw new Error(`Failed to fetch contents at ${path}: ${response.statusText}`)
  }

  const contents = await response.json()

  if (!Array.isArray(contents)) {
    // Single file - decode base64 content
    if (contents.type === 'file' && contents.content) {
      try {
        const decodedContent = atob(contents.content.replace(/\n/g, ''))
        files.set(contents.path, decodedContent)
        onProgress?.(`Downloaded ${contents.path}`, Math.min(95, 30 + (files.size * 2)))
      } catch (error) {
        console.warn(`Failed to decode file ${contents.path}:`, error)
      }
    }
    return
  }

  // Process all items
  const promises: Promise<void>[] = []

  for (const item of contents) {
    if (item.type === 'file') {
      // For files, we need to fetch the full content (the listing only has truncated content)
      promises.push(
        fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${item.path}?ref=${branch}`, { headers })
          .then(res => res.json())
          .then(fileData => {
            if (fileData.content) {
              try {
                const decodedContent = atob(fileData.content.replace(/\n/g, ''))
                files.set(item.path, decodedContent)
                onProgress?.(`Downloaded ${item.path}`, Math.min(95, 30 + (files.size * 2)))
              } catch (error) {
                console.warn(`Failed to decode file ${item.path}:`, error)
              }
            }
          })
          .catch(error => {
            console.warn(`Failed to fetch file ${item.path}:`, error)
          })
      )
    } else if (item.type === 'dir') {
      // Recursively fetch directory contents
      promises.push(
        fetchRepoContents(owner, repo, item.path, branch, headers, files, onProgress)
      )
    }
  }

  await Promise.all(promises)
}

/**
 * Download a GitHub repository using the GitHub API
 * This avoids CORS issues with direct ZIP downloads
 */
export async function downloadGitHubRepo(
  repoUrl: string,
  onProgress?: (message: string, progress: number) => void
): Promise<Map<string, string>> {
  const repoInfo = parseGitHubUrl(repoUrl)

  if (!repoInfo) {
    throw new Error('Invalid GitHub repository URL')
  }

  const { owner, repo } = repoInfo
  const githubToken = getApiKey('github')

  onProgress?.('Fetching repository information...', 10)

  // Get default branch
  const repoApiUrl = `https://api.github.com/repos/${owner}/${repo}`
  const headers: HeadersInit = {
    'Accept': 'application/vnd.github.v3+json'
  }

  if (githubToken) {
    headers['Authorization'] = `Bearer ${githubToken}`
  }

  const repoResponse = await fetch(repoApiUrl, { headers })

  if (!repoResponse.ok) {
    const errorData = await repoResponse.json().catch(() => ({}))
    if (repoResponse.status === 404) {
      throw new Error(`Repository not found: ${owner}/${repo}. Make sure the repository exists and you have access to it.`)
    } else if (repoResponse.status === 401) {
      throw new Error('Invalid GitHub token. Please check your GitHub PAT in Settings.')
    }
    throw new Error(`Failed to fetch repository info: ${errorData.message || repoResponse.statusText}`)
  }

  const repoData = await repoResponse.json()
  const defaultBranch = repoData.default_branch || 'main'

  // Check if repository is empty (has no commits)
  if (repoData.size === 0) {
    console.log('[downloadGitHubRepo] Repository is empty, returning empty file list')
    onProgress?.('Repository is empty - no files to download', 90)
    onProgress?.('Repository cloned successfully!', 100)
    return new Map<string, string>()
  }

  onProgress?.('Downloading repository files...', 30)

  // Fetch all files recursively using GitHub API
  const files = new Map<string, string>()
  try {
    await fetchRepoContents(owner, repo, '', defaultBranch, headers, files, onProgress)
  } catch (error) {
    // If we get a 404, the repo might be empty despite what the API says
    if (error instanceof Error && error.message.includes('404')) {
      console.log('[downloadGitHubRepo] Got 404 fetching contents, repository appears empty')
      onProgress?.('Repository is empty - no files to download', 90)
      onProgress?.('Repository cloned successfully!', 100)
      return new Map<string, string>()
    }
    throw error
  }

  onProgress?.('Repository downloaded successfully!', 100)

  return files
}

/**
 * Check if a GitHub token is valid
 */
export async function validateGitHubToken(token: string): Promise<boolean> {
  try {
    const response = await fetch('https://api.github.com/user', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/vnd.github.v3+json'
      }
    })

    return response.ok
  } catch {
    return false
  }
}

/**
 * Get repository default branch
 */
export async function getDefaultBranch(
  owner: string,
  repo: string
): Promise<string> {
  const githubToken = getApiKey('github')
  const headers: HeadersInit = {
    'Accept': 'application/vnd.github.v3+json'
  }

  if (githubToken) {
    headers['Authorization'] = `Bearer ${githubToken}`
  }

  const url = `https://api.github.com/repos/${owner}/${repo}`
  const response = await fetch(url, { headers })

  if (!response.ok) {
    throw new Error(`Failed to fetch repository info: ${response.statusText}`)
  }

  const data = await response.json()
  return data.default_branch || 'main'
}

/**
 * Get the latest commit SHA for a branch
 */
export async function getLatestCommitSha(
  owner: string,
  repo: string,
  branch: string = 'main'
): Promise<string> {
  const githubToken = getApiKey('github')
  const headers: HeadersInit = {
    'Accept': 'application/vnd.github.v3+json'
  }

  if (githubToken) {
    headers['Authorization'] = `Bearer ${githubToken}`
  }

  const url = `https://api.github.com/repos/${owner}/${repo}/branches/${branch}`
  const response = await fetch(url, { headers })

  if (!response.ok) {
    throw new Error(`Failed to fetch branch info: ${response.statusText}`)
  }

  const data = await response.json()
  return data.commit.sha
}

/**
 * Check if there are updates available for a GitHub repository
 */
export async function checkForUpdates(
  owner: string,
  repo: string,
  branch: string,
  currentSha: string
): Promise<{ hasUpdates: boolean; latestSha: string; commitsBehind?: number }> {
  const latestSha = await getLatestCommitSha(owner, repo, branch)

  if (latestSha === currentSha) {
    return { hasUpdates: false, latestSha }
  }

  // Get commits between current and latest
  const githubToken = getApiKey('github')
  const headers: HeadersInit = {
    'Accept': 'application/vnd.github.v3+json'
  }

  if (githubToken) {
    headers['Authorization'] = `Bearer ${githubToken}`
  }

  try {
    const compareUrl = `https://api.github.com/repos/${owner}/${repo}/compare/${currentSha}...${latestSha}`
    const response = await fetch(compareUrl, { headers })

    if (response.ok) {
      const data = await response.json()
      return {
        hasUpdates: true,
        latestSha,
        commitsBehind: data.total_commits || data.commits?.length || 0
      }
    }
  } catch (error) {
    console.warn('Failed to get commit count:', error)
  }

  return { hasUpdates: true, latestSha }
}

/**
 * Get changed files between two commits
 */
export async function getChangedFiles(
  owner: string,
  repo: string,
  baseSha: string,
  headSha: string
): Promise<Array<{ filename: string; status: 'added' | 'modified' | 'removed' }>> {
  const githubToken = getApiKey('github')
  const headers: HeadersInit = {
    'Accept': 'application/vnd.github.v3+json'
  }

  if (githubToken) {
    headers['Authorization'] = `Bearer ${githubToken}`
  }

  const compareUrl = `https://api.github.com/repos/${owner}/${repo}/compare/${baseSha}...${headSha}`
  const response = await fetch(compareUrl, { headers })

  if (!response.ok) {
    throw new Error(`Failed to compare commits: ${response.statusText}`)
  }

  const data = await response.json()
  return data.files.map((file: any) => ({
    filename: file.filename,
    status: file.status
  }))
}

